import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";
import { faDownload } from "@fortawesome/free-solid-svg-icons/faDownload";
import { faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons/faExternalLinkAlt";
import { faPlay } from "@fortawesome/free-solid-svg-icons/faPlay";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import styled from "styled-components";

import { AtiraIcon } from "../../../../components/AtiraIcon";
import { Button } from "../../../../components/Button";
import { Clickable } from "../../../../components/Clickable";
import { Flex } from "../../../../components/Flex";
import { FontSize, Text } from "../../../../components/Text";
import { Modal } from "../../../../components/modal/Modal";
import { useStepContext } from "../../../../components/stepper/StepperContext";
import { integrationSliceSelectors } from "../../../../redux/integration/integration.selector";
import { useAppSelector } from "../../../../redux/store";
import { Rounded } from "../../../../theme/Rounded";
import { Spacing } from "../../../../theme/Spacing";
import Scenario from "../../../../utils/integration/Atira Space Instant Facebook Leads Integration.blueprint.json";

const Container = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.s};
  width: 80%;
  align-self: flex-start;
`;

const Title = styled(Text)`
  font-size: ${FontSize.l};
  color: ${({ theme }) => theme.main};
  font-weight: bold;
  margin-bottom: ${Spacing.s};
`;

const StyledButton = styled(Button)`
  width: fit-content;
  margin: 0;
  margin-top: ${Spacing.m};
  display: block;

  &:hover {
    background-color: ${({ theme }) => theme.lightMain};
  }
`;

const StyledList = styled.ol`
  margin: 0;
  padding: 0;
  list-style-position: inside;
  list-style-type: disc;
  display: flex;
  flex-direction: column;
  gap: ${Spacing.l};

  li::marker {
    color: ${({ theme }) => theme.main};
  }

  li p {
    display: inline !important;
  }
`;

const StyledLink = styled.a`
  display: flex;
  align-items: center;
  gap: ${Spacing.s};
  text-decoration: none;
  background-color: ${({ theme }) => theme.main};
  color: ${({ theme }) => theme.sub};
  border-radius: ${Rounded.md};
  padding: ${Spacing.s} ${Spacing.l};
  width: fit-content;
  transition: all 0.3s;
  margin-top: ${Spacing.m};
  font-size: ${FontSize.s};

  &:hover {
    background-color: ${({ theme }) => theme.lightMain};
    color: ${({ theme }) => theme.sub};
  }
`;

export const InetgrationFacebookImportIntegrationStep: React.FC = () => {
  const [helpModalOpen, setHelpModalOpen] = useState(false);

  const facebookIntegration = useAppSelector(
    integrationSliceSelectors.selectFBIntegration,
  );
  const { onNext } = useStepContext();
  const { t } = useTranslation();

  const onConnectWithMake = () => window.open("https://eu2.make.com", "_blank");

  return (
    <Container>
      <Flex gap="s">
        <Title>{t("integration.add-modal.facebook.make.import.title")}</Title>
        <Clickable onClick={() => setHelpModalOpen(true)}>
          <Button margin="none" padding={"s"} icon={faPlay}>
            {t("integration.add-modal.facebook.make.import.subtitle")}
          </Button>
        </Clickable>
      </Flex>

      <StyledList>
        <li>
          <Text>
            {t("integration.add-modal.facebook.make.step1.description")}
          </Text>

          <StyledLink
            href={`data:text/json;charset=utf-8,${encodeURIComponent(
              JSON.stringify(Scenario),
            )}`}
            download="Atira Space-Instant-Facebook-Leads-Integration.blueprint.json"
          >
            <AtiraIcon icon={faDownload} />
            {t("integration.add-modal.facebook.make.step1.button")}
          </StyledLink>
        </li>

        <li>
          <Text style={{ marginBottom: Spacing.s }}>
            {t("integration.add-modal.facebook.make.step2.description")}
            <Flex style={{ display: "inline-flex" }} gap="s">
              <Link
                target="_blank"
                to="https://www.make.com/en/integrations/facebook-lead-ads"
              >
                {t("integration.add-modal.facebook.make.step2.link")}
              </Link>
              <AtiraIcon icon={faExternalLinkAlt} />
            </Flex>
          </Text>
          <Flex flexDirection="column" gap="xs">
            <Flex>
              <ul>
                <li>
                  <Text>
                    {t(
                      "integration.add-modal.facebook.make.step2.description-3",
                    )}
                  </Text>
                </li>
                <li>
                  <Text>
                    {t(
                      "integration.add-modal.facebook.make.step2.description-4",
                    )}
                  </Text>
                </li>
                <li>
                  <Text>
                    {t(
                      "integration.add-modal.facebook.make.step2.description-2",
                    )}
                  </Text>
                </li>
              </ul>
            </Flex>
          </Flex>

          <StyledButton
            icon={faExternalLinkAlt}
            onClick={onConnectWithMake}
            disabled={!facebookIntegration}
          >
            {t("integration.add-modal.facebook.make.step2.button")}
          </StyledButton>
        </li>

        <li>
          <Text>
            {t("integration.add-modal.facebook.make.step3.description")}
          </Text>
          <Flex flexDirection="column">
            <Text>{t("integration.add-modal.facebook.make.step3.button")}</Text>
          </Flex>
        </li>

        <li>
          <Text>
            {t("integration.add-modal.facebook.make.step4.description")}
          </Text>
          <Flex>
            <Button icon={faCheck} onClick={onNext}>
              {t("integration.add-modal.facebook.make.step4.button")}
            </Button>
          </Flex>
        </li>
      </StyledList>

      <Modal
        open={helpModalOpen}
        width={800}
        destroyOnClose
        onClose={() => setHelpModalOpen(false)}
        title={t("integration.add-modal.facebook.make.import.helpModal.title")}
      >
        <Flex flexDirection="column" gap="m">
          <Text>
            {t(
              "integration.add-modal.facebook.make.import.helpModal.description",
            )}
          </Text>

          <div style={{ position: "relative", paddingTop: "56.25%" }}>
            <iframe
              title="How to import Atira Space - Instant Facebook Leads Integration scenario to Make.com"
              src="https://iframe.mediadelivery.net/embed/484036/5ae07d75-19b5-40dd-854a-68a86585a519?autoplay=true&loop=true&muted=true&preload=true&responsive=true"
              loading="lazy"
              style={{
                border: 0,
                position: "absolute",
                top: 0,
                left: 0,
                height: "100%",
                width: "100%",
                borderRadius: 12,
                boxShadow: "0 2px 16px rgba(0,0,0,0.12)",
                background: "#000",
              }}
              allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;"
              allowFullScreen
            ></iframe>
          </div>
        </Flex>
      </Modal>
    </Container>
  );
};
